import { useCallback, useEffect, useRef, useState } from "react";
import {
  GOOGLE_ERROR_PARAM,
  GOOGLE_HANDOFF_PARAM,
  OWNER_ROLE_ID,
  SESSION_STORAGE_KEY,
  isTimedOut,
} from "@shared/protocol";
import { BRAND, C, FONT_MONO, FONT_SANS, hexA } from "@/theme";
import { AppProvider, can, friendState, roleOf, useApp, userById } from "@/state/store";
import { Auth, googleErrorMessage, parseAuth } from "@/components/Auth";
import { ChatView } from "@/components/ChatView";
import { CloseIcon } from "@/components/icons";
import { MembersPanel } from "@/components/MembersPanel";
import { MobileApp } from "@/components/MobileApp";
import { ProfileEditor } from "@/components/ProfileEditor";
import { AvisoBanido } from "@/components/AvisoBanido";
import { PainelFundadores } from "@/components/PainelFundadores";
import { ProfileModal } from "@/components/ProfileModal";
import { QuickSwitcher } from "@/components/QuickSwitcher";
import { SettingsModal } from "@/components/SettingsModal";
import { ServerRail } from "@/components/ServerRail";
import { Sidebar } from "@/components/Sidebar";
import { UserContextMenu, type UserMenuAction } from "@/components/UserContextMenu";
import { VoiceView } from "@/components/VoiceView";

const MOBILE_QUERY = "(max-width: 900px)";

/* -------------------------------------------------------------------- */
/* Sessao                                                                */
/* -------------------------------------------------------------------- */

/** Navegadores em modo privado podem barrar o localStorage — nunca deixe quebrar a tela. */
function readToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    /* sem armazenamento: a sessao vale so enquanto a aba estiver aberta */
  }
}

function clearToken(): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* nada a limpar */
  }
}

/* -------------------------------------------------------------------- */
/* Retorno do login com Google                                           */
/* -------------------------------------------------------------------- */

type GoogleReturn = { code: string | null; error: string | null };

/**
 * Le e **apaga** da URL o que o callback do Google deixou. Apagar na mesma
 * passada importa por dois motivos: o codigo e de uso unico (recarregar a
 * pagina com ele ainda no endereco daria um erro sem sentido pra quem le), e
 * em desenvolvimento o `StrictMode` monta o efeito duas vezes — como a
 * limpeza acontece antes de qualquer `await`, a segunda passada ja nao acha
 * nada e nao tenta trocar o mesmo codigo de novo.
 */
function takeGoogleReturn(): GoogleReturn {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return { code: null, error: null };
  }

  const code = params.get(GOOGLE_HANDOFF_PARAM);
  const error = params.get(GOOGLE_ERROR_PARAM);
  if (code === null && error === null) return { code: null, error: null };

  params.delete(GOOGLE_HANDOFF_PARAM);
  params.delete(GOOGLE_ERROR_PARAM);
  const query = params.toString();
  const clean = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  try {
    window.history.replaceState(null, "", clean);
  } catch {
    // Sem historico manipulavel (algum navegador embutido): o codigo ja foi
    // lido de qualquer jeito, so o endereco fica feio.
  }
  return { code, error };
}

type Gate =
  | { kind: "checking" }
  | { kind: "offline" }
  | { kind: "anon" }
  | { kind: "authed"; token: string };

/* -------------------------------------------------------------------- */
/* Ganchos                                                               */
/* -------------------------------------------------------------------- */

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setMobile(event.matches);
    setMobile(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return mobile;
}

/** Mesmo `applyVars()` do desenho, aplicado no elemento raiz do documento. */
function useAccentVars(accent: string): void {
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--ac", accent);
    root.style.setProperty("--acs", hexA(accent, 0.14));
    root.style.setProperty("--mpy", "6px");
    root.style.setProperty("--ipy", "7px");
  }, [accent]);
}

function reloadApp(): void {
  window.location.reload();
}

/* -------------------------------------------------------------------- */
/* Telas de estado                                                       */
/* -------------------------------------------------------------------- */

/**
 * Splash de arranque. Quem se apresenta aqui é o app: nenhum servidor foi
 * escolhido ainda. Antes desenhava a caixa azul com a letra das configurações,
 * que no arranque é sempre o valor padrão — ou seja, o "L" da marca antiga.
 */
function LoadingScreen({ label }: { label: string }) {
  return (
    <div
      style={{
        height: "100dvh",
        display: "grid",
        placeItems: "center",
        background: C.app,
        color: C.text,
        fontFamily: FONT_SANS,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
          animation: "fadein 200ms ease",
        }}
      >
        <img src={BRAND.mark} alt="LFRCORD" style={{ width: 60, height: 60, display: "block" }} />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "999px",
              background: "var(--ac,#1F4FD8)",
              animation: "blinkdot 1.4s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: ".14em",
              color: C.textLabelDim,
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}

function FailureScreen({
  label,
  message,
  onRetry,
}: {
  label: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      style={{
        height: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: "20px",
        background: C.app,
        color: C.text,
        fontFamily: FONT_SANS,
      }}
    >
      <div
        style={{
          width: "min(380px,92vw)",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: "16px",
          overflow: "hidden",
          background: "#1d1d1d",
          animation: "pop 200ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "11px 16px",
            borderBottom: "1px solid #292929",
          }}
        >
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "999px",
              background: C.red,
              animation: "blinkdot 1.4s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: "9.5px",
              fontWeight: 600,
              letterSpacing: ".1em",
              color: C.red,
            }}
          >
            {label}
          </span>
        </div>
        <div style={{ padding: "16px 18px 18px" }}>
          <div style={{ fontSize: "13.5px", lineHeight: 1.55, color: C.textBody }}>{message}</div>
          <button
            className="hov-bright act-press"
            onClick={onRetry}
            style={{
              width: "100%",
              marginTop: "16px",
              padding: "11px 0",
              border: 0,
              borderRadius: "10px",
              cursor: "pointer",
              background: "var(--ac,#1F4FD8)",
              color: C.ink,
              fontSize: "13.5px",
              fontWeight: 700,
              transition: "transform 120ms ease",
            }}
          >
            Tentar de novo
          </button>
        </div>
      </div>
    </div>
  );
}

/** Faixa fixa no topo quando a conexao cai com o app ja carregado. */
function ErrorBand() {
  const { state } = useApp();
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "9px 16px",
        background: "#1d1d1d",
        borderBottom: `1px solid ${C.lineStrong}`,
        fontFamily: FONT_SANS,
        animation: "fadein 150ms ease",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          flex: "0 0 auto",
          borderRadius: "999px",
          background: C.red,
          animation: "blinkdot 1.4s ease-in-out infinite",
        }}
      />
      <span
        style={{
          flex: "0 0 auto",
          fontFamily: FONT_MONO,
          fontSize: "9.5px",
          fontWeight: 600,
          letterSpacing: ".1em",
          color: C.red,
        }}
      >
        CONEXÃO PERDIDA
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: "12.5px",
          color: C.textMuted,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {state.error ?? "A conexão com o servidor caiu."}
      </span>
      <button
        className="hov-bg-26"
        onClick={reloadApp}
        style={{
          height: "26px",
          flex: "0 0 auto",
          padding: "0 12px",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: "8px",
          cursor: "pointer",
          background: "transparent",
          color: C.textSoft,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        Tentar de novo
      </button>
    </div>
  );
}

/** Faixa da tela de entrada explicando por que a sessao terminou. */
function KickedBand({ reason, onDismiss }: { reason: string; onDismiss: () => void }) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "9px 16px",
        background: "#1d1d1d",
        borderBottom: `1px solid ${hexA(C.red, 0.35)}`,
        fontFamily: FONT_SANS,
        animation: "fadein 150ms ease",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          flex: "0 0 auto",
          borderRadius: "999px",
          background: C.red,
        }}
      />
      <span
        style={{
          flex: "0 0 auto",
          fontFamily: FONT_MONO,
          fontSize: "9.5px",
          fontWeight: 600,
          letterSpacing: ".1em",
          color: C.red,
        }}
      >
        REMOVIDO DA COMUNIDADE
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: "12.5px",
          lineHeight: 1.45,
          color: C.textMuted,
          overflowWrap: "break-word",
        }}
      >
        {reason}
      </span>
      <button
        className="hov-bg-26-fg"
        onClick={onDismiss}
        title="Fechar"
        style={{
          width: "24px",
          height: "24px",
          flex: "0 0 auto",
          display: "grid",
          placeItems: "center",
          border: 0,
          borderRadius: "7px",
          cursor: "pointer",
          background: "transparent",
          color: C.textFaint,
        }}
      >
        <CloseIcon size={13} />
      </button>
    </div>
  );
}

/** Aviso discreto no rodape; some sozinho em 5s. */
function Toast() {
  const { state, actions } = useApp();
  const notice = state.notice;
  const dismiss = actions.dismissNotice;

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => dismiss(), 5_000);
    return () => window.clearTimeout(timer);
  }, [dismiss, notice]);

  if (!notice) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        bottom: "18px",
        transform: "translateX(-50%)",
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        maxWidth: "min(520px,92vw)",
        padding: "9px 9px 9px 14px",
        border: `1px solid ${C.lineStrong}`,
        borderRadius: "12px",
        background: C.raised,
        boxShadow: "0 10px 30px rgba(0,0,0,.45)",
        fontFamily: FONT_SANS,
        animation: "slideup 200ms ease",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          flex: "0 0 auto",
          borderRadius: "999px",
          background: C.red,
        }}
      />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: "12.5px",
          lineHeight: 1.45,
          color: C.textSoft,
          overflowWrap: "break-word",
        }}
      >
        {notice}
      </span>
      <button
        className="hov-bg-26-fg"
        onClick={dismiss}
        title="Fechar"
        style={{
          width: "24px",
          height: "24px",
          flex: "0 0 auto",
          display: "grid",
          placeItems: "center",
          border: 0,
          borderRadius: "7px",
          cursor: "pointer",
          background: "transparent",
          color: C.textFaint,
        }}
      >
        <CloseIcon size={13} />
      </button>
    </div>
  );
}

/** Faixa discreta enquanto o socket tenta voltar; some sozinha ao reconectar. */
function ReconnectBand() {
  const { state } = useApp();
  // Com a conexao ja dada como perdida quem fala e a `ErrorBand`, que oferece
  // recarregar a pagina — duas faixas empilhadas so tirariam espaco do topo.
  if (!state.reconnecting || state.phase === "error") return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 71,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "9px 16px",
        background: "#1d1d1d",
        borderBottom: `1px solid ${hexA(C.amber, 0.32)}`,
        fontFamily: FONT_SANS,
        animation: "fadein 150ms ease",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          flex: "0 0 auto",
          borderRadius: "999px",
          background: C.amber,
          animation: "blinkdot 1.4s ease-in-out infinite",
        }}
      />
      <span
        style={{
          flex: "0 0 auto",
          fontFamily: FONT_MONO,
          fontSize: "9.5px",
          fontWeight: 600,
          letterSpacing: ".1em",
          color: C.amber,
        }}
      >
        RECONECTANDO…
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: "12.5px",
          color: C.textMuted,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        A conexão caiu. Estamos tentando voltar — nada do que você escreveu se perde.
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Atalhos de teclado                                                    */
/* -------------------------------------------------------------------- */

type Shortcut = { keys: string[]; label: string };
type ShortcutGroup = { title: string; items: Shortcut[] };

/** No macOS o modificador e o Command; o atalho em si e o mesmo. */
function modifierLabel(): string {
  return /Mac|iPhone|iPad|iPod/i.test(window.navigator.userAgent) ? "⌘" : "Ctrl";
}

function shortcutGroups(mod: string): ShortcutGroup[] {
  return [
    {
      title: "NAVEGAÇÃO",
      items: [
        { keys: [mod, "K"], label: "Abrir o seletor rápido de canais, salas e membros" },
        { keys: [mod, "/"], label: "Mostrar e esconder esta lista" },
        { keys: ["Esc"], label: "Fechar o que estiver aberto" },
      ],
    },
    {
      title: "CONVERSA",
      items: [
        { keys: ["Enter"], label: "Enviar a mensagem" },
        { keys: ["Shift", "Enter"], label: "Quebrar linha sem enviar" },
        { keys: ["↑"], label: "Editar a sua última mensagem, com o compositor vazio" },
        { keys: ["Esc"], label: "Cancelar a resposta ou a edição em curso" },
      ],
    },
    {
      title: "VOZ",
      items: [
        {
          keys: ["Espaço"],
          label: "Falar enquanto a tecla estiver pressionada, com apertar para falar ligado",
        },
      ],
    },
  ];
}

/** Tecla desenhada como no rodape do compositor: mono, caixa escura, borda fina. */
function KeyCap({ label }: { label: string }) {
  return (
    <span
      style={{
        display: "inline-grid",
        placeItems: "center",
        minWidth: "22px",
        padding: "3px 7px",
        border: `1px solid ${C.lineStrong}`,
        borderRadius: "6px",
        background: C.field,
        fontFamily: FONT_MONO,
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: ".06em",
        color: C.textSoft,
      }}
    >
      {label}
    </span>
  );
}

function ShortcutsPanel({ onClose }: { onClose: () => void }) {
  const groups = shortcutGroups(modifierLabel());

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 65,
        display: "grid",
        placeItems: "center",
        padding: "20px",
        background: "rgba(0,0,0,.62)",
        fontFamily: FONT_SANS,
        animation: "fadein 150ms ease",
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          width: "min(440px,100%)",
          maxHeight: "82vh",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: "16px",
          overflow: "hidden",
          background: "#1d1d1d",
          color: C.text,
          animation: "pop 200ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "16px 18px 12px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "16px", fontWeight: 700 }}>Atalhos do teclado</div>
            <div
              style={{
                marginTop: "3px",
                fontFamily: FONT_MONO,
                fontSize: "9.5px",
                letterSpacing: ".1em",
                color: C.textGhost,
              }}
            >
              LFRCORD · TECLADO
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            className="hov-bg-26-f2"
            style={{
              width: "30px",
              height: "30px",
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              padding: 0,
              border: `1px solid ${C.lineStrong}`,
              borderRadius: "999px",
              cursor: "pointer",
              background: "transparent",
              color: C.textMuted,
            }}
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 18px 18px" }}>
          {groups.map(group => (
            <div key={group.title} style={{ marginTop: "10px" }}>
              <div
                style={{
                  marginBottom: "2px",
                  fontFamily: FONT_MONO,
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: ".12em",
                  color: C.textFaint,
                }}
              >
                {group.title}
              </div>
              {group.items.map(item => (
                <div
                  key={`${group.title}:${item.keys.join("+")}:${item.label}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "10px 2px",
                    borderBottom: `1px solid ${C.lineRow}`,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "12.5px",
                      lineHeight: 1.45,
                      color: C.textBody,
                    }}
                  >
                    {item.label}
                  </span>
                  <span
                    style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "5px" }}
                  >
                    {item.keys.map((key, index) => (
                      <span
                        key={`${key}-${index}`}
                        style={{ display: "flex", alignItems: "center", gap: "5px" }}
                      >
                        {index > 0 ? (
                          <span style={{ fontSize: "11px", color: C.textGhost }}>+</span>
                        ) : null}
                        <KeyCap label={key} />
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* Layout                                                                */
/* -------------------------------------------------------------------- */

/**
 * So uma parte das 21 acoes do menu tem algo de verdade pra fazer no LFRCORD
 * hoje (sem sistema de amigos, sem visualizacao de moderador, sem
 * mover-entre-salas) — as que nao tem, avisam "em breve" em vez de fingir
 * que funcionaram.
 */
function handleUserMenuAction(
  state: ReturnType<typeof useApp>["state"],
  actions: ReturnType<typeof useApp>["actions"],
  action: UserMenuAction,
  userId: string,
): void {
  const volumeOf = (id: string): number => state.volumes[id] ?? 1;
  switch (action) {
    case "PROFILE":
      actions.openProfile(userId);
      return;
    case "MENTION": {
      const key = state.activeKey;
      const current = state.drafts[key] ?? "";
      const handle = userById(state, userId)?.handle;
      if (!handle) return;
      actions.setDraft(`${current}${current && !current.endsWith(" ") ? " " : ""}@${handle} `);
      return;
    }
    case "MESSAGE":
      actions.messageUser(userId);
      return;
    case "CALL":
      actions.inviteToVoice(userId);
      return;
    case "KICK":
      actions.kickMember(userId);
      return;
    case "BAN":
      actions.banMember(userId);
      return;
    // A duração já foi escolhida no submenu e aplicada pelo callback
    // direto (`onTimeout`); aqui só se evita o aviso de "não existe".
    case "TIMEOUT":
      return;
    case "COPY_USER_ID":
      void navigator.clipboard.writeText(userId).then(
        () => actions.setNotice("ID copiado."),
        () => actions.setNotice("Não foi possível copiar o ID."),
      );
      return;
    case "TOGGLE_MUTE":
      actions.setVolume(userId, volumeOf(userId) > 0 ? 0 : 1);
      return;
    case "SERVER_VOICE_MUTE": {
      const target = userById(state, userId);
      actions.setServerMute(userId, !(target?.serverMuted ?? false));
      return;
    }
    case "SERVER_AUDIO_DISABLE": {
      const target = userById(state, userId);
      actions.setServerDeafen(userId, !(target?.serverDeafened ?? false));
      return;
    }
    case "DISCONNECT":
      actions.disconnectFromVoice(userId);
      return;
    case "IGNORE":
      actions.toggleIgnoreUser(userId);
      return;
    case "BLOCK":
      actions.toggleBlockUser(userId);
      return;
    // Copiava `window.location.origin` — a raiz do site, que não é convite
    // nenhum: leva à tela de cadastro e não entra em servidor algum. O
    // convite de verdade é o código que `servers:invite` devolve, o mesmo
    // que o modal da barra de servidores já usa.
    case "COPY_INVITE_LINK":
      actions.copyServerInvite();
      return;
    // O trabalho de verdade já rodou pelo callback direto (onAssignRole /
    // onMoveToChannel) antes de "onAction" disparar — aqui só evita o aviso
    // de "não existe" depois de uma ação que, na real, já funcionou.
    case "ASSIGN_ROLE":
    case "MOVE_TO_CHANNEL":
      return;
    default:
      actions.setNotice("Essa ação ainda não existe no LFRCORD.");
  }
}

/** Menu de contexto de usuario: um so mount pra tela inteira, pra funcionar tanto na voz quanto na lista de membros. */
function GlobalUserContextMenu() {
  const { state, actions } = useApp();
  if (!state.contextMenu) return null;
  const targetId = state.contextMenu.userId;
  const target = userById(state, targetId);
  const volumeOf = (id: string): number => state.volumes[id] ?? 1;

  // Espelho da checagem que vale de verdade no servidor
  // (server/permissions.ts, moderationCheck): só esconde o que o servidor já
  // recusaria, para o menu não oferecer ação que volta como erro. Posição
  // menor = mais graduado.
  const meRole = state.me ? roleOf(state, state.me.id) : undefined;
  const targetRole = target ? roleOf(state, target.id) : undefined;
  const servidorAberto = state.servers.find(server => server.id === state.activeServerId);
  // "Manda no servidor" = dono dele ou fundador da plataforma. Era
  // `me.roleId === OWNER_ROLE_ID`, o cargo GLOBAL da conta — que não existe
  // mais como fonte de verdade desde que o cargo passou a ser por servidor.
  const souDono = state.platformFounder || servidorAberto?.ownerId === state.me?.id;
  const alvoManda =
    target !== undefined &&
    servidorAberto !== undefined &&
    servidorAberto.ownerId === target.id;
  const outranksTarget =
    target?.id === state.me?.id ||
    // Entre pares vale: quem também manda pode agir sobre quem manda.
    (souDono && (!alvoManda || souDono)) ||
    (!alvoManda &&
      meRole !== undefined &&
      targetRole !== undefined &&
      // `>=` e não `>`: mesmo nível passa a valer, como no servidor.
      targetRole.position >= meRole.position);
  const canManageRoles = can(state, "cargos") && outranksTarget;
  // Mesma logica de hierarquia acima, aplicada tambem a mute/deafen/mover/
  // desconectar — sem isso o menu ofereceria uma acao que o servidor
  // (moderationCheck) so ia recusar por posicao.
  const canModerateVoice = can(state, "silenciar") && outranksTarget;
  // "Desconectar da chamada" e a unica acao que le canModerate — o servidor
  // (members:voiceDisconnect) so aceita quem tem "silenciar", entao o menu
  // nao pode oferecer o botao pra quem so tem "expulsar" (o servidor recusaria).
  const canModerate = can(state, "silenciar") && outranksTarget;

  return (
    <UserContextMenu
      userId={targetId}
      username={target?.name ?? "Membro"}
      // O menu já tinha "Chamar" e "Remover amigo" condicionados a isto, mas a
      // constante falsa deixava os dois como código morto desde sempre.
      isFriend={friendState(state, targetId) === "amigo"}
      isSelf={targetId === state.me?.id}
      serverPermissions={{
        canMute: canModerateVoice,
        canDeafen: canModerateVoice,
        canMoveMembers: canModerateVoice,
        canManageRoles,
        canModerate,
      }}
      onAction={(action, userId) => handleUserMenuAction(state, actions, action, userId)}
      x={state.contextMenu.x}
      y={state.contextMenu.y}
      onClose={actions.closeContextMenu}
      volumePercent={Math.round(volumeOf(targetId) * 100)}
      onVolumeChange={(userId, percent) => actions.setVolume(userId, Math.min(100, percent) / 100)}
      muted={volumeOf(targetId) === 0}
      ignored={state.ignored.includes(targetId)}
      blocked={state.blocked.includes(targetId)}
      serverMuted={target?.serverMuted ?? false}
      serverDeafened={target?.serverDeafened ?? false}
      timedOut={isTimedOut(target)}
      canInviteToVoice={state.voiceId !== null && targetId !== state.me?.id}
      onTimeout={(id, untilMs) => actions.timeoutMember(id, untilMs)}
      roleOptions={
        canManageRoles
          ? [...state.roles]
              .filter(
                role =>
                  souDono ||
                  // `>=`: dar um cargo do proprio nivel passa a valer, igual ao
                  // que o servidor aceita desde a regra de pares.
                  (meRole !== undefined && role.position >= meRole.position),
              )
              .sort((a, b) => a.position - b.position)
              .map(role => ({
                id: role.id,
                name: role.name,
                color: role.color,
                // TODOS os cargos da pessoa ficam marcados, nao so o mais
                // alto: o submenu virou uma lista de ligar/desligar, e mostrar
                // um so faria parecer que dar outro substituiria este.
                selected: target?.roleIds.includes(role.id) ?? false,
              }))
          : undefined
      }
      onAssignRole={(userId, roleId) => actions.setMemberRole(userId, roleId)}
      channelOptions={
        canModerateVoice
          ? [...state.voiceChannels]
              .sort((a, b) => a.position - b.position)
              .map(channel => ({ id: channel.id, name: channel.name }))
          : undefined
      }
      onMoveToChannel={(userId, voiceId) => actions.moveMemberToVoice(userId, voiceId)}
    />
  );
}

function DesktopLayout() {
  const { state, actions } = useApp();
  const isVoice = state.activeKey.startsWith("voice:");
  const showMembers = state.membersOpen && state.activeKey.startsWith("ch:");

  return (
    <div
      style={{
        height: "100dvh",
        display: "flex",
        background: C.app,
        color: C.text,
        fontFamily: FONT_SANS,
        overflow: "hidden",
        minWidth: "960px",
      }}
    >
      <ServerRail />
      <Sidebar />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: C.stage,
        }}
      >
        {isVoice ? <VoiceView /> : <ChatView />}
      </div>
      {showMembers ? <MembersPanel /> : null}
      <ProfileModal />
      <SettingsModal />
      {/*
        O painel e o aviso de banimento ficam por ULTIMO, e nesta ordem: o
        painel cobre o app, e o aviso cobre o painel. Quem foi banido nao
        continua administrando nada enquanto nao clica em "ciente".
      */}
      {state.painelAberto ? <PainelFundadores onFechar={actions.fecharPainelFundadores} /> : null}
      <AvisoBanido />
      {state.profileEditorOpen ? <ProfileEditor /> : null}
      <GlobalUserContextMenu />
    </div>
  );
}

/**
 * Guarda o motivo da expulsao antes que a arvore seja desmontada e, se a loja
 * nao tiver encerrado a sessao sozinha, encerra por ela.
 */
function KickWatcher({ onKicked }: { onKicked: (reason: string) => void }) {
  const { state, actions } = useApp();
  const kicked = state.kicked;
  const logout = actions.logout;
  const handled = useRef(false);

  useEffect(() => {
    if (kicked === null || handled.current) return;
    handled.current = true;
    onKicked(kicked);
    logout();
  }, [kicked, logout, onKicked]);

  return null;
}

/** Decide desktop x mobile e cuida das telas de conexao. */
function Shell({ onKicked }: { onKicked: (reason: string) => void }) {
  const { state, actions } = useApp();
  const mobile = useIsMobile();
  useAccentVars(state.settings.accent);

  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const booted = state.me !== null;
  const openSwitcher = actions.openSwitcher;
  /** Lido dentro do ouvinte sem precisar registra-lo de novo a cada abertura. */
  const switcherOpen = useRef(state.switcherOpen);
  switcherOpen.current = state.switcherOpen;

  useEffect(() => {
    if (!booted) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        if (event.key === "Escape") setShortcutsOpen(false);
        return;
      }
      if (event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === "k") {
        // O proprio seletor rapido escuta Ctrl+K; se ele ja tratou o evento
        // (ou ja esta aberto), aqui nao ha nada a fazer.
        if (event.defaultPrevented || switcherOpen.current) return;
        event.preventDefault();
        setShortcutsOpen(false);
        openSwitcher();
        return;
      }
      if (key === "/" || key === "?") {
        event.preventDefault();
        setShortcutsOpen(previous => !previous);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [booted, openSwitcher]);

  let content = mobile ? <MobileApp /> : <DesktopLayout />;
  if (!booted) {
    content =
      state.phase === "error" ? (
        <FailureScreen
          label="SEM CONEXÃO"
          message={state.error ?? "Não foi possível conectar ao servidor do LFRCORD."}
          onRetry={reloadApp}
        />
      ) : (
        <LoadingScreen label="CONECTANDO" />
      );
  }

  return (
    <>
      {content}
      {booted ? <QuickSwitcher /> : null}
      {booted && shortcutsOpen ? (
        <ShortcutsPanel onClose={() => setShortcutsOpen(false)} />
      ) : null}
      {booted && state.phase === "error" ? <ErrorBand /> : null}
      {booted ? <ReconnectBand /> : null}
      <Toast />
      <KickWatcher onKicked={onKicked} />
    </>
  );
}

/* -------------------------------------------------------------------- */
/* Portao de sessao                                                      */
/* -------------------------------------------------------------------- */

export function App() {
  const [gate, setGate] = useState<Gate>({ kind: "checking" });
  const [kicked, setKicked] = useState<string | null>(null);
  /** Recado pra tela de entrada — hoje, só o que deu errado no login com Google. */
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  /** Motivo capturado enquanto o provedor ainda esta montado. */
  const kickReason = useRef<string | null>(null);

  const validate = useCallback(async (): Promise<void> => {
    // Antes de qualquer coisa: o retorno do Google chega pela URL e precisa
    // sair dela na mesma passada.
    const returned = takeGoogleReturn();
    if (returned.error !== null) {
      setAuthNotice(googleErrorMessage(returned.error));
      setGate({ kind: "anon" });
      return;
    }
    if (returned.code !== null) {
      setGate({ kind: "checking" });
      try {
        const response = await fetch("/api/auth/google/exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: returned.code }),
        });
        const payload: unknown = await response.json().catch(() => null);
        if (response.ok) {
          const session = parseAuth(payload);
          if (session !== null) {
            writeToken(session.sessionToken);
            setGate({ kind: "authed", token: session.sessionToken });
            return;
          }
          setAuthNotice("O servidor respondeu em um formato inesperado.");
        } else {
          // O 401 aqui é o código de uso único já gasto ou vencido, e a
          // mensagem do servidor explica isso melhor que qualquer genérica.
          const told =
            typeof payload === "object" && payload !== null
              ? (payload as Record<string, unknown>).error
              : null;
          setAuthNotice(
            typeof told === "string" && told.trim() ? told : googleErrorMessage("servidor"),
          );
        }
      } catch {
        setGate({ kind: "offline" });
        return;
      }
      setGate({ kind: "anon" });
      return;
    }

    const stored = readToken();
    if (!stored) {
      setGate({ kind: "anon" });
      return;
    }
    setGate({ kind: "checking" });
    try {
      const response = await fetch("/api/session", {
        headers: { Authorization: `Bearer ${stored}` },
      });
      if (response.ok) {
        setGate({ kind: "authed", token: stored });
        return;
      }
      // Token recusado pelo servidor: descarta e volta para a tela de entrada.
      clearToken();
      setGate({ kind: "anon" });
    } catch {
      setGate({ kind: "offline" });
    }
  }, []);

  useEffect(() => {
    void validate();
  }, [validate]);

  const handleSession = useCallback((token: string) => {
    kickReason.current = null;
    setKicked(null);
    setAuthNotice(null);
    writeToken(token);
    setGate({ kind: "authed", token });
  }, []);

  const dismissNotice = useCallback(() => setAuthNotice(null), []);

  /**
   * Token novo emitido no meio da sessão (trocar senha, encerrar as outras
   * sessões). Guardar aqui é o que faz o socket reconectar com ele: o valor
   * desce como `sessionToken` para o provedor, que reabre a conexão. Sem isso
   * a próxima reconexão levaria o token antigo, já invalidado.
   */
  const handleSessionToken = useCallback((token: string) => {
    writeToken(token);
    setGate(prev => (prev.kind === "authed" ? { kind: "authed", token } : prev));
  }, []);

  const handleKicked = useCallback((reason: string) => {
    kickReason.current = reason;
  }, []);

  const handleLogout = useCallback(() => {
    clearToken();
    // A expulsao chega como estado do provedor; adiar a troca de tela por um
    // ciclo garante que o motivo ja tenha sido capturado antes do desmonte.
    window.setTimeout(() => {
      if (kickReason.current !== null) {
        setKicked(kickReason.current);
        kickReason.current = null;
      }
      setGate({ kind: "anon" });
    }, 0);
  }, []);

  // Sem ícone: antes de a sessão resolver não há servidor nenhum aberto, então
  // quem aparece é a marca do app.
  if (gate.kind === "checking") return <LoadingScreen label="CARREGANDO" />;

  if (gate.kind === "offline") {
    return (
      <FailureScreen
        label="SEM CONEXÃO"
        message="Não foi possível falar com o servidor. Confira se o LFRCORD está no ar e tente de novo."
        onRetry={() => {
          void validate();
        }}
      />
    );
  }

  if (gate.kind === "anon") {
    return (
      <>
        <Auth onSession={handleSession} notice={authNotice} onDismissNotice={dismissNotice} />
        {kicked !== null ? (
          <KickedBand reason={kicked} onDismiss={() => setKicked(null)} />
        ) : null}
      </>
    );
  }

  return (
    <AppProvider
      sessionToken={gate.token}
      onLogout={handleLogout}
      onSessionToken={handleSessionToken}
    >
      <Shell onKicked={handleKicked} />
    </AppProvider>
  );
}
